import type { AxiosError, AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import type { Logger } from '@zg-rentals/logger-node';

import axios from 'axios';
import { createCookieString } from './cookies';

export interface RequesterInterface extends AxiosRequestConfig {
  api?: string;
  proxyPath?: string;
  logger?: Logger;
}

export function createRequestHeaders({
  brand = '',
  ip = '',
  userAgent = '',
  url,
  isNode = typeof window === 'undefined',
  buildId = '',
  method,
  logger,
  cookies = {},
  headers = {},
}: {
  brand?: string;
  ip?: string;
  userAgent?: string;
  url?: string;
  isNode?: boolean;
  buildId?: string;
  cookies?: Record<string, string>;
  method?: Method;
  logger?: Logger;
  headers?: Record<string, string>;
}) {
  const nodeHeaders: Record<string, string> = {};

  if (isNode) {
    nodeHeaders['user-agent'] = userAgent;
    nodeHeaders['x-brand'] = brand;
    nodeHeaders['x-client-ip'] = ip;
    nodeHeaders.cookie = createCookieString(cookies);
  }

  const madeHeaders: Record<string, string> = {
    Accept: '*/*',
    'x-build-id': buildId,
    ...nodeHeaders,
    ...headers,
  };

  if (url) {
    madeHeaders['x-newrelic-transaction'] = url;
  }

  if (!madeHeaders['Content-Type'] && method) {
    madeHeaders['Content-Type'] = method.toLowerCase() === 'get' ? 'application/json,text/html' : 'application/json';
  }

  logger && logger.debug(madeHeaders, 'requester customized headers');

  return madeHeaders;
}

export function makeBaseUrl(api?: string, proxy?: string) {
  if (api && api.endsWith('/')) {
    api = api.substring(0, api.length - 1);
  }
  if (proxy) {
    if (proxy.startsWith('/')) {
      proxy = proxy.substring(1);
    }
    if (proxy.endsWith('/')) {
      proxy = proxy.substring(0, proxy.length - 1);
    }
  }
  if (typeof window === 'undefined') {
    return api ? `${api}/` : undefined;
  } else {
    if (api && proxy) {
      return `${api}/${proxy}/`;
    } else if (api) {
      return `${api}/`;
    } else if (proxy) {
      return `${location.origin}/${proxy}/`;
    }
  }
}

export function requester(opts: RequesterInterface): AxiosInstance {
  const {
    logger,
    withCredentials = true,
    api,
    proxyPath,
    baseURL = makeBaseUrl(api, proxyPath),
    timeout = 120_000,
    ...args
  } = opts;

  const config: AxiosRequestConfig = {
    baseURL,
    timeout,
    withCredentials,
    ...args,
  };

  logger && logger.debug(config, 'creating axios instance');

  return axios.create(config);
}

export function logRequesterError({ error, logger }: { error: AxiosError; logger?: Logger }) {
  if (!logger) {
    throw error;
  }

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    logger.error(
      {
        resData: error.response.data,
        resStatus: error.response.status,
        resHeaders: error.response.headers,
      },
      'response error',
    );
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    logger.error(
      {
        req: error.request,
      },
      'no response received',
    );
  } else {
    // Something happened in setting up the request that triggered an Error
    logger.error(
      {
        errMsg: error.message,
      },
      'req config error',
    );
  }

  throw error;
}

export default requester;
